import type { ApiTypingsLookup } from "@/api";

import { registerPreviewHandler, type PreviewHandler } from "../registry";
import {
  handleChannelContribution,
  handleInsightsOverview,
  handleIntegrationStatsPullStatus,
  handleJourneySankey,
  handleRecommendationCards,
  handleSourceTouchBreakdown,
  handleTopJourneys,
} from "../attribution/handlers";
import {
  handleGetAppointmentTimeline,
  handleGetCampaignAnalysisTimeSeries,
  handleGetCampaignDeviceList,
  handleGetDeviceEventStream,
  handleGetMarketingPerformanceAnalysis,
  handleGetMarketingPerformanceAnalysisCustomer,
} from "./ad-performance";
import {
  handleGetAiChatConversation,
  handleListAiChatConversations,
  handleSaveAiChatConversation,
} from "./ai-chat";
import {
  handleAnalyticsFunnelQuery,
  handleFunnelQuery,
  handleListFunnels,
} from "./funnels";
import { handleListActiveDestinations } from "./destinations";
import {
  handleGetDynamicEvents,
  handleGetEventDefsEnriched,
} from "./event-defs";
import {
  handleGetSurvey,
  handleGetSurveyResponses,
  handleGetSurveyResults,
  handleGetSurveyTimeseries,
  handleGetSurveys,
} from "./surveys";
import {
  handleAnalyticsEventQuery,
  handleAnalyticsTopConversions,
  handleAnalyticsTopEvents,
} from "./web-analytics";

// ApiTypingsLookup declares survey responses as parsed FE shapes (e.g.
// Survey[]), but front/src/surveys/api.ts deserializes the wire shape
// ({ surveys: BackendSurvey[] }). Our handlers return the wire shape so
// the deserializer keeps working; this cast bridges the disconnect
// without forcing a refactor of api/surveys.ts.
function asPreviewHandler<K extends keyof ApiTypingsLookup>(
  fn: (params: ApiTypingsLookup[K]["request"]) => unknown,
): PreviewHandler<K> {
  return fn as PreviewHandler<K>;
}

export function registerAllPreviewModeHandlers(): void {
  registerPreviewHandler("list_funnels", handleListFunnels);
  registerPreviewHandler("analytics_funnel_query", handleAnalyticsFunnelQuery);
  registerPreviewHandler("funnel_query", handleFunnelQuery);
  registerPreviewHandler("get_event_defs_enriched", handleGetEventDefsEnriched);
  registerPreviewHandler("get_dynamic_events", handleGetDynamicEvents);
  registerPreviewHandler(
    "list_active_destinations",
    handleListActiveDestinations,
  );

  registerPreviewHandler("analytics_event_query", handleAnalyticsEventQuery);
  registerPreviewHandler(
    "analytics_top_conversions",
    handleAnalyticsTopConversions,
  );
  registerPreviewHandler("analytics_top_events", handleAnalyticsTopEvents);

  registerPreviewHandler(
    "get_surveys",
    asPreviewHandler<"get_surveys">(handleGetSurveys),
  );
  registerPreviewHandler(
    "get_survey",
    asPreviewHandler<"get_survey">(handleGetSurvey),
  );
  registerPreviewHandler(
    "get_survey_results",
    asPreviewHandler<"get_survey_results">(handleGetSurveyResults),
  );
  registerPreviewHandler(
    "get_survey_timeseries",
    asPreviewHandler<"get_survey_timeseries">(handleGetSurveyTimeseries),
  );
  registerPreviewHandler(
    "get_survey_responses",
    asPreviewHandler<"get_survey_responses">(handleGetSurveyResponses),
  );

  registerPreviewHandler(
    "ai_chat_list_conversations",
    handleListAiChatConversations,
  );
  registerPreviewHandler(
    "ai_chat_get_conversation",
    handleGetAiChatConversation,
  );
  registerPreviewHandler(
    "ai_chat_save_conversation",
    handleSaveAiChatConversation,
  );

  registerPreviewHandler("get_insights_overview", handleInsightsOverview);
  registerPreviewHandler(
    "get_insights_source_touch_breakdown",
    handleSourceTouchBreakdown,
  );
  registerPreviewHandler("get_journey_sankey", handleJourneySankey);
  registerPreviewHandler("get_top_journeys", handleTopJourneys);
  registerPreviewHandler("get_channel_contribution", handleChannelContribution);
  registerPreviewHandler(
    "get_recommendation_cards_with_state",
    handleRecommendationCards,
  );
  registerPreviewHandler(
    "get_integration_stats_pull_status",
    handleIntegrationStatsPullStatus,
  );

  registerPreviewHandler(
    "get_ehr_marketing_analysis",
    handleGetMarketingPerformanceAnalysis,
  );
  registerPreviewHandler(
    "get_ehr_marketing_analysis_customer",
    handleGetMarketingPerformanceAnalysisCustomer,
  );
  registerPreviewHandler(
    "get_ehr_marketing_analysis_timeseries",
    handleGetCampaignAnalysisTimeSeries,
  );
  registerPreviewHandler(
    "get_ehr_marketing_campaign_devices",
    handleGetCampaignDeviceList,
  );
  registerPreviewHandler(
    "get_ehr_marketing_campaign_device_event_stream",
    handleGetDeviceEventStream,
  );
  registerPreviewHandler(
    "get_ehr_marketing_appointment_timeline",
    handleGetAppointmentTimeline,
  );
}
